import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from './AuthContext';

const PaymentContext = createContext();

export const usePayments = () => {
    const context = useContext(PaymentContext);
    if (!context) {
        throw new Error('usePayments deve ser usado dentro de um PaymentProvider');
    }
    return context;
};

export const PaymentProvider = ({ children }) => {
    const { user } = useAuth();
    const [payments, setPayments] = useState([]);
    const [contracts, setContracts] = useState({});
    const [loading, setLoading] = useState(true);

    // Move fetchPayments outside useEffect to expose it
    const fetchPayments = async () => {
        setLoading(true);
        const { data, error } = await supabase
            .from('payments')
            .select('*')
            .order('id', { ascending: false });

        if (error) console.error("Error fetching payments:", error);
        else {
            const formattedData = (data || []).map(p => ({
                ...p,
                userId: p.userid,
                userName: p.username,
                totalValue: p.totalvalue,
                createdAt: p.createdat,
                processedAt: p.processedat,
                processedBy: p.processedby,
                standardValue: p.requestdata?.standardValue,
                extraValue: p.requestdata?.extraValue,
                observation: p.requestdata?.observation,
                invoiceSent: p.requestdata?.invoiceSent,
                rejectionReason: p.requestdata?.rejectionReason,
                _original: p
            }));
            setPayments(formattedData);
        }
        setLoading(false);
    };

    const fetchContracts = async () => {
        const { data, error } = await supabase.from('contracts').select('*');
        if (error) {
            console.error("Error fetching contracts:", error);
        } else {
            const contractsMap = {};
            data?.forEach(item => { contractsMap[item.id] = item.data; });
            setContracts(contractsMap);
        }
    };

    useEffect(() => {
        fetchPayments();
        fetchContracts();

        const paymentsChannel = supabase.channel('payments-monitor')
            .on('postgres_changes', { event: '*', schema: 'public', table: 'payments' }, fetchPayments)
            .subscribe();

        const contractsChannel = supabase.channel('contracts-changes')
            .on('postgres_changes', { event: '*', schema: 'public', table: 'contracts' }, fetchContracts)
            .subscribe();

        return () => {
            supabase.removeChannel(paymentsChannel);
            supabase.removeChannel(contractsChannel);
        };
    }, []);

    // Create a new payment request
    const createRequest = async (requestData) => {
        const id = `REQ-${Date.now()}`;
        const newRequest = {
            id,
            userid: user.discordId,
            username: user.name,
            status: 'pending',
            totalvalue: requestData.totalValue,
            requestdata: {
                standardValue: requestData.standardValue,
                extraValue: requestData.extraValue,
                observation: requestData.observation,
                invoiceSent: requestData.invoiceSent
            }
        };

        // Optimistic Update: Add to state immediately
        const optimisticPayment = {
            ...newRequest,
            userId: user.discordId,
            userName: user.name,
            totalValue: requestData.totalValue,
            createdAt: new Date().toISOString(),
            standardValue: requestData.standardValue,
            extraValue: requestData.extraValue,
            observation: requestData.observation,
            invoiceSent: requestData.invoiceSent,
            _original: newRequest
        };
        setPayments(prev => [optimisticPayment, ...prev]);

        try {
            const { error } = await supabase.from('payments').insert(newRequest);
            if (error) throw error;
            return newRequest;
        } catch (error) {
            console.error("Error creating payment request:", error);
            // Rollback on error
            setPayments(prev => prev.filter(p => p.id !== id));
            throw error;
        }
    };

    // Update request status (Admin)
    const updateRequestStatus = async (requestId, status, processedBy, rejectionReason = null) => {
        try {
            // 1. Fetch current data to preserve other fields in requestdata
            const { data: currentItem, error: fetchError } = await supabase
                .from('payments')
                .select('requestdata')
                .eq('id', requestId)
                .single();

            if (fetchError) throw fetchError;

            // 2. Prepare updated requestdata
            const updatedRequestData = {
                ...(currentItem?.requestdata || {}),
                rejectionReason
            };

            // 3. Optimistic Update
            setPayments(prev => prev.map(p =>
                p.id === requestId
                    ? {
                        ...p,
                        status,
                        processedBy,
                        processedAt: new Date().toISOString(),
                        rejectionReason
                    }
                    : p
            ));

            // 4. Update in Supabase
            const { error } = await supabase
                .from('payments')
                .update({
                    status,
                    processedat: new Date().toISOString(),
                    processedby: processedBy,
                    requestdata: updatedRequestData
                })
                .eq('id', requestId);

            if (error) throw error;
        } catch (error) {
            console.error("Error updating payment status:", error);
            // Revert could be implemented here by re-fetching, but currently just logging error
        }
    };

    // Get user's contract details
    const getUserContract = (userId) => {
        // Mock default contract if none exists
        // In a real app, this should be set by Admin when hiring staff
        if (!contracts[userId]) {
            // FALLBACK TO AUTH DATA IF AVAILABLE
            if (user && user.discordId === userId && user.staffDetails?.contractData) {
                const cData = user.staffDetails.contractData;
                const createdAt = user.createdAt || new Date().toISOString();

                // Calculate end date based on term (e.g. "3 meses" or number 3)
                const duration = parseInt(cData.contractTerm?.match(/(\d+)/)?.[1] || cData.contractDuration || cData.contractMonths || 3);
                const unit = cData.contractUnit || 'months';

                const startDate = new Date(createdAt);
                let endDate;

                if (cData.endDate) {
                    endDate = new Date(cData.endDate);
                } else {
                    endDate = new Date(startDate);
                    if (unit === 'days') {
                        endDate.setDate(endDate.getDate() + duration);
                    } else {
                        endDate.setMonth(endDate.getMonth() + duration);
                    }
                }

                return {
                    monthlyValue: parseFloat(cData.contractValue) || 0,
                    startDate: createdAt,
                    endDate: endDate.toISOString(),
                    status: 'active', // Default to active if registered
                    unit,
                    duration
                };
            }

            return {
                monthlyValue: 0,
                startDate: new Date().toISOString(),
                endDate: null, // "A definir"
                status: 'pending' // active, pending, ended
            };
        }
        if (contracts[userId]) {
            return {
                ...contracts[userId],
                monthlyValue: parseFloat(contracts[userId].monthlyValue || contracts[userId].contractValue) || 0
            };
        }
        return contracts[userId];
    };

    // Delete all payments for a specific month (YYYY-MM)
    const deleteAllMonthlyPayments = async (monthStr) => {
        try {
            // Calculate start and end date for the month
            const [year, month] = monthStr.split('-');
            const startDate = new Date(year, month - 1, 1).toISOString();
            // End date is start of next month
            const endDate = new Date(year, month, 1).toISOString();

            console.log(`Deleting payments between ${startDate} and ${endDate}`);

            const { error } = await supabase
                .from('payments')
                .delete()
                .gte('createdat', startDate)
                .lt('createdat', endDate);

            if (error) throw error;

            // Remove locally
            setPayments(prev => prev.filter(p => {
                const pDate = new Date(p.createdAt);
                const pMonthStr = `${pDate.getFullYear()}-${String(pDate.getMonth() + 1).padStart(2, '0')}`;
                return pMonthStr !== monthStr;
            }));

            return { success: true };
        } catch (error) {
            console.error("Error deleting monthly payments:", error);
            return { success: false, error };
        }
    };

    // Stats for Admin Dashboard
    const getAdminStats = () => {
        const currentMonth = new Date().getMonth();
        const currentYear = new Date().getFullYear();

        const approvedThisMonth = payments
            .filter(p => p.status === 'approved' &&
                new Date(p.processedat || p.processed_at).getMonth() === currentMonth &&
                new Date(p.processedat || p.processed_at).getFullYear() === currentYear)
            .reduce((sum, p) => sum + (parseFloat(p.totalValue) || 0), 0);

        const awaitingPayment = payments
            .filter(p => p.status === 'pending')
            .reduce((sum, p) => sum + (parseFloat(p.totalValue) || 0), 0);

        return {
            pendingCount: payments.filter(p => p.status === 'pending').length,
            approvedCount: payments.filter(p => p.status === 'approved').length,
            rejectedCount: payments.filter(p => p.status === 'rejected').length,
            awaitingPaymentValue: awaitingPayment,
            approvedThisMonthValue: approvedThisMonth
        };
    };

    const refreshPayments = async () => {
        await Promise.all([fetchPayments(), fetchContracts()]);
    };

    // Update contract data
    const updateContract = async (userId, contractData) => {
        try {
            const { error } = await supabase
                .from('contracts')
                .upsert({
                    id: userId,
                    data: contractData
                });

            if (error) throw error;

            setContracts(prev => ({
                ...prev,
                [userId]: contractData
            }));

            return { success: true };
        } catch (error) {
            console.error("Error updating contract:", error);
            return { success: false, error };
        }
    };

    const value = {
        payments,
        contracts,
        loading,
        createRequest,
        updateRequestStatus,
        getUserContract,
        updateContract,
        getAdminStats,
        deleteAllMonthlyPayments,
        refreshPayments
    };

    return (
        <PaymentContext.Provider value={value}>
            {children}
        </PaymentContext.Provider>
    );
};
